import {Component, ComponentRef, EventEmitter, Injector, Output, ViewChild, ViewContainerRef} from "@angular/core";
import {Subscription} from "rxjs";
import {FUIStackedComponentRefView} from "./stacked-component-ref-view";
import {FUIStackedComponentRef, FUIStackedPageService} from "../stacked-page.service";
import {FUICloseConfirmation, FUIStackedPageContextService} from "../stacked-page-context.service";
import {FUIEventsService} from "../../events/events.service";

@Component({
    selector: "fui-stacked-page-modal",
    styleUrls: ["./stacked-page-modal.component.scss"],
    templateUrl: "./stacked-page-modal.component.html"
})
export class FUIStackedPageModalComponent {

    @ViewChild("modalwindowmain", {read: ViewContainerRef}) viewContainerRef: ViewContainerRef;

    @Output() cancelled: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() confirmed: EventEmitter<boolean> = new EventEmitter<boolean>();

    public isOpen: boolean;

    public views: FUIStackedComponentRefView[] = [];

    private requestingCancel: boolean;

    constructor(private stackedPageService: FUIStackedPageService) {
    }

    public addComponentToView(stackedComponentRef: FUIStackedComponentRef): void {
        setTimeout(() => {
            const injector: Injector = Injector.create({
                providers: [
                    {
                        provide: FUIStackedPageContextService,
                        useFactory: () => {
                            return new FUIStackedPageContextService(stackedComponentRef.params);
                        }
                    },
                    FUIEventsService
                ],
                parent: this.viewContainerRef.injector
            });

            const extendedModeService: FUIStackedPageContextService = injector.get(FUIStackedPageContextService);
            const onConfirmSubscription: Subscription = extendedModeService.onConfirm.subscribe(result => this.confirmResult(result));
            const onCancelSubscription: Subscription = extendedModeService.onCancel.subscribe(result => this.cancel());

            const componentRef: ComponentRef<any> = this.viewContainerRef.createComponent(stackedComponentRef.component, {injector: injector});

            this.pushView(new FUIStackedComponentRefView(stackedComponentRef,
                this.viewContainerRef.insert(componentRef.hostView),
                extendedModeService,
                [
                    onConfirmSubscription,
                    onCancelSubscription
                ]
            ));
        });
    }

    closeModal(): void {
        while (this.views.length > 0) {
            const closeConfirmation: FUICloseConfirmation = new FUICloseConfirmation();
            this.getCurrentView().context.onCloseRequest.next(closeConfirmation);
            if (closeConfirmation.canClose instanceof Promise) {
                closeConfirmation.canClose.then((value) => {
                    if (value) {
                        this.cancel();
                        this.closeModal();
                    }
                });
                break;
            } else {
                if (closeConfirmation.canClose) {
                    this.cancel();
                } else {
                    break;
                }
            }
        }
    }

    cancel(): void {
        if (this.getCurrentView().componentRef.resolveOnCancel) {
            this.getCurrentView().componentRef.confirmCallback.next(undefined);
        }
        this.getCurrentView().componentRef.confirmCallback.complete();
        this.popView();
    }

    requestCancel(): void {
        if (this.requestingCancel) {
            return;
        }
        this.requestingCancel = true;
        const closeConfirmation: FUICloseConfirmation = new FUICloseConfirmation();
        this.getCurrentView().context.onCloseRequest.next(closeConfirmation);
        if (closeConfirmation.canClose instanceof Promise) {
            closeConfirmation.canClose.then((value) => {
                if (value) this.cancel();
                this.requestingCancel = false;
            });
        } else {
            if (closeConfirmation.canClose) {
                this.cancel();
            }

            this.requestingCancel = false;
        }
    }

    confirmResult(result: Array<any>): void {
        this.getCurrentView().componentRef.confirmCallback.next(result);
        this.getCurrentView().componentRef.confirmCallback.complete();
        this.popView();
    }

    pushView(stackedComponentRefView: FUIStackedComponentRefView): void {
        if (this.views.length > 0) {
            this.viewContainerRef.detach(0);
        }
        this.views.push(stackedComponentRefView);
        this.isOpen = true;
    }

    popView(): void {
        const stackedComponentRefView: FUIStackedComponentRefView = this.views.pop();
        stackedComponentRefView.subscription.forEach(subscription => subscription.unsubscribe());
        stackedComponentRefView.viewRef.destroy();

        if (this.views.length > 0) {
            this.viewContainerRef.insert(this.views[this.views.length - 1].viewRef);
        } else {
            this.isOpen = false;
            this.stackedPageService.destroyModal();
        }
    }

    getCurrentView(): FUIStackedComponentRefView {
        return this.views[this.views.length - 1];
    }
}
